// Extends Vex.Flow.Annotation to allow class styling for accidentals:

import { Annotation as VexAnnotation } from 'vexflow';
import utils from '../utils';

const JUSTIFICATION = {
  LEFT: 1,
  CENTER: 2,
  RIGHT: 3,
  CENTER_STEM: 4,
};

const VERTICALJUSTIFICATION = {
  TOP: 1,
  CENTER: 2,
  BOTTOM: 3,
  CENTER_STEM: 4,
};

interface Options {
  font?: {
    family?: string;
    size?: number;
    weight?: string;
  };
  class?: string;
  verticalJustification?: 'top' | 'center' | 'bottom' | 'center_stem';
  justification?: 'left' | 'center' | 'right' | 'center_stem';
  fill?: string;
}

/**
 * Create an annotation, and attach it to a noteHolder as
 * noteHolder.annotation and it will render.
 *
 * @param {string} text -- text to display
 * @param {object} options
 * @param {string} options.jusification "left", "center", "right", "center_stem"
 * @param {string} options.verticalJustification "top", "center", "bottom", "center_stem"
 * @param {string} options.class string
 * @param {object} options.font {family, size, weight}
 * @param {string} options.fill: color-string
 */
export default class Annotation extends VexAnnotation {
  // text: string;

  options: Options;

  private fontOptions: {
    family: string;
    size: number;
    weight: string;
  };

  widthCoefficient: number = 1;

  getText() {
    return this.text;
  }

  // private context: Vex.Flow.SVGContext;

  constructor(text, options: Options = {}) {
    super(text);
    this.text = text;
    this.options = this.options || {};
    utils.merge(this.options, options);

    this.setAlignment(this.options);

    this.fontOptions = {
      family: 'arial',
      size: 10,
      weight: 'normal',
      ...(this.options.font || {}),
    };
  }

  setClass(className) {
    this.options.class = className;
  }

  // @ts-ignore -- too much work to handle everywhere width is gotten in formatting
  // and rendering.
  get width(): number {
    const context = this.getContext();
    if (!context) {
      return this.text.length * 6 * this.widthCoefficient;
    }
    return context.measureText(this.text).width * this.widthCoefficient;
  }

  // @ts-ignore
  // eslint-disable-next-line
  set width(n: number) {}

  // /*
  // * @param fontStyleObj:
  // *   family
  // *   size
  // *   weight
  // */
  // setFont(fontStyleObj) {
  //   this.font = {
  //     family: fontStyleObj.family || this.fontOptions.family,
  //     size: fontStyleObj.size || this.fontOptions.size,
  //     weight: fontStyleObj.weight || this.fontOptions.weight,
  //   };
  // }

  setAlignment(options: Options) {
    options = options || this.options;
    const justification = options.justification || 'center';
    const verticalJustification = options.verticalJustification || 'bottom';
    if (JUSTIFICATION.hasOwnProperty(justification.toUpperCase())) {
      this.options.justification = justification;
      (this as any).setJustification(JUSTIFICATION[justification.toUpperCase()]);
    }
    if (VERTICALJUSTIFICATION.hasOwnProperty(verticalJustification.toUpperCase())) {
      this.options.verticalJustification = verticalJustification;
      (this as any).setVerticalJustification(
        VERTICALJUSTIFICATION[verticalJustification.toUpperCase()]
      );
    }
  }

  draw() {
    const context = this.getContext();
    context.save();
    context.setFillStyle(this.options.fill || 'black');
    const poppedClass = (this as any).context.attributes.class;
    if (this.options.class) {
      (this as any).context.attributes.class = this.options.class;
    }
    if (this.options.class === 'accidentals') {
      this.fontOptions.family = 'uTheory Musical';
    }

    context.setFont(this.fontOptions.family, this.fontOptions.size, this.fontOptions.weight);
    super.draw();
    if (poppedClass) {
      (this as any).context.attributes.class = poppedClass;
    } else {
      delete (this as any).context.attributes.class;
    }
    (this as any).context.restore();
  }

  clone() {
    const options = {
      ...this.options,
      font: this.fontOptions,
    };
    return new Annotation(this.text, options);
  }
}
